import { Box, Button, Paper, Typography } from '@mui/material'
import Check from '@mui/icons-material/Check'
import Error from '@mui/icons-material/Error'
import PendingActions from '@mui/icons-material/PendingActions'
import { useAPI } from 'contexts/APIProvider'
import React from 'react'
import FormField from 'components/FormField'
import { trim } from 'lodash'
import Nurse from 'logic/Nurse'
import NurseProfileError from 'errors/NurseProfileError'

interface HygienistReferenceProps {
    nurseId: number
}

type Status = 'pending' | 'success' | 'error'

const HygienistReference: React.FC<HygienistReferenceProps> = ({ nurseId }) => {
    const { api } = useAPI()
    const [nurse, setNurse] = React.useState<Nurse>()
    const [referenceFromName, setReferenceFromName] = React.useState<string>('')
    const [referenceFromEmail, setReferenceFromEmail] = React.useState<string>('')
    const [loading, setLoading] = React.useState<boolean>(false)
    const [status, setStatus] = React.useState<Status>('pending')
    const [error, setError] = React.useState<NurseProfileError>()

    const fetchNurse = React.useCallback(async () => {
        const nurse = await api.getNurse(nurseId)
        setNurse(nurse)
        if (nurse?.reference_from_name && nurse?.reference_from_email) {
            setReferenceFromName(nurse.reference_from_name)
            setReferenceFromEmail(nurse.reference_from_email)
            setStatus('success')
        }
    }, [api, nurseId])

    const save = React.useCallback(async () => {
        try {
            setLoading(true)
            await api.editNurse({ reference_from_name: referenceFromName, reference_from_email: referenceFromEmail }, nurseId)
            setStatus('success')
            setError(undefined)
        } catch (e) {
            setStatus('error')
            if (e instanceof NurseProfileError) {
                setError(e)
              } else {
                console.error('unhandled exception', e)
              }
        } finally {
            setLoading(false)
        }
    }, [api, referenceFromName, referenceFromEmail, nurseId])

    const backgroundColor = React.useMemo(() => {
        if (status === 'error') {
            return 'error.light'
        } else if (status === 'success') {
            return 'success.light'
        }
    }, [status])

    const body = React.useMemo(() => {
        if (status === 'error') {
            return (
                <Typography variant='body2'>
                    Failed to submit your reference info. Please try again.
                </Typography>
            )
        } else if (status === 'success') {
            return (
                <Typography variant='body2'>
                    Our team member will contact your reference to verify your UK work experience. <br />
                    If you want to update your reference info, just re-submit the form again.
                </Typography>
            )
        } else {
            return (
                <Typography variant='body2'>
                    We only accept hygienists with UK work experience. <br />
                    Please provide the name and email of a reference who can verify your work experience.
                </Typography>
            )
        }
    }, [status, referenceFromName, referenceFromEmail])

    const icon = React.useMemo(() => {
        if (status === 'error') {
            return <Error fontSize='large' sx={{ color: 'error.main' }} />
        } else if (status === 'success') {
            return <Check fontSize='large' sx={{ color: 'success.main' }} />
        } else {
            return <PendingActions fontSize='large' sx={{ color: 'black' }} />
        }
    }, [status])

    React.useEffect(() => {
        fetchNurse()
    }, [fetchNurse])

    return (
        <Paper sx={{
            padding: 1,
            display: 'grid',
            gridTemplateColumns: icon ? ['1fr 4fr', '1fr 9fr'] : '0 1fr',
            gridTemplateRows: 'auto',
            gridTemplateAreas: `
        "icon title"
        "icon content"`,
            backgroundColor: backgroundColor,
        }}>
            <Box sx={{ gridArea: 'icon', justifySelf: 'center', alignSelf: 'center' }}>
                {icon}
            </Box>
            <Typography sx={{ gridArea: 'title' }} variant='h5'>
                Work Reference (Required for Hygienist)
            </Typography>
            <Box sx={{ gridArea: 'content' }}>
                {body}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormField 
                        label='Name' 
                        value={referenceFromName} 
                        onChange={e => setReferenceFromName(e.target.value)} 
                        errors={error?.reference_from_name ?? []}
                    />
                    <FormField 
                        label='Email' 
                        value={referenceFromEmail} 
                        onChange={e => setReferenceFromEmail(trim(e.target.value))} 
                        errors={error?.reference_from_email ?? []}
                    />
                    <Button size='small' variant='contained' disabled={loading} onClick={save}>
                        {loading ? 'Please Wait ...' : 'Submit'}
                    </Button>
                </Box>
            </Box>
        </Paper>
    )
}

export default HygienistReference